import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { _className, Button, Grid, LL_H_C, LL_V_L, ModuleFE_Toaster, SimpleListAdapter, TS_DropDown, TS_PropRenderer, TS_Tabs, TS_Toggle } from '@nu-art/thunderstorm-frontend/index';
import { filterDuplicates, filterInstances, sortArray } from '@nu-art/ts-common';
import { ModuleFE_ContextRule, MultiSelect_ContextRule, QScrollWrapper } from '@app/hcs-frontend';
import './Editor_Organization_AdvisorConfigurations.scss';
import { Component_SwitchView } from '@nu-art/permissions-frontend/ui/Component_SwitchView';
import { ModuleFE_Pathway } from '@app/pathway-frontend';
import { DefaultEditor_InputText, DefaultEditor_InputText_Optional } from '@app/styles/frontend/ui-manager/ui-components';
import { TS_EditableItemComponentProto } from '@nu-art/thunderstorm-frontend/components/TS_EditableItemComponent/TS_EditableItemComponent';
import { ViewKey_Edit, ViewKey_View } from '../../../consts.js';
import { getSelectedContextRulesIds } from './Viewer_PathwaysByContextRules/utils.js';
import { getMatchingPathwaysByContextRules } from './utils.js';
import { PathwaysByContextRules } from './PathwayByContextRules/index.js';
import { ModuleFE_Artifact } from '../../../../../_entity/artifact/index.js';
import { versionStringToNumeric } from '@nu-art/thunderstorm-shared';
import { MultiSelect_NoteTemplate } from '@app/advisor-content-frontend/_entity/note-template/ui-components';
import { Dropdown_ABTestValue } from '../../../../../_entity/instance/advisor-configuration/ui-components.js';
export class Editor_Organization_AdvisorConfigurations extends TS_EditableItemComponentProto {
    // ######################## Lifecycle ########################
    deriveStateFromProps(nextProps, state) {
        state = super.deriveStateFromProps(nextProps, state);
        state.editMode = nextProps.editMode;
        return state;
    }
    // ######################## Logic ########################
    getViewMode = () => this.state.editMode ? ViewKey_Edit : ViewKey_View;
    getPathwaysOfType = (type, pathwayIds) => {
        const pathways = filterInstances(pathwayIds?.map(id => {
            const pathway = ModuleFE_Pathway.cache.unique(id);
            if (!pathway) {
                this.logError(`Could not get pathway for id ${id}`);
                return;
            }
            if (pathway.archived)
                return;
            return pathway;
        })) ?? [];
        return pathways.filter(pathway => pathway?.type === type);
    };
    getTabs = () => {
        return [
            {
                uid: 'advisor-configuration-info',
                title: 'Configuration Information',
                content: this.renderConfigurationInformation,
            },
            {
                uid: 'pathways',
                title: 'Pathways Details',
                content: this.renderPathwaysDetails
            },
            {
                uid: 'notes',
                title: 'Notes',
                content: this.render_Notes,
            },
            {
                uid: 'ab-testing',
                title: 'AB Testing',
                content: this.render_ABTesting,
            }
        ];
    };
    // ######################## Render ########################
    render() {
        return _jsx(LL_H_C, { id: 'editor-organization-configurations', children: this.renderTabs() });
    }
    // ######################## Render - All Tabs ########################
    renderTabs = () => {
        if (!this.state.editable)
            return;
        return _jsx(TS_Tabs, { tabs: this.getTabs() });
    };
    // ######################## Render - Configuration Editor ########################
    renderPathwaysDetails = () => {
        if (!this.state.editable)
            return _jsx(_Fragment, {});
        const className = _className(this.state.editMode && 'edit-mode');
        return (_jsxs("div", { className: "form-wrapper", children: [_jsx("div", { className: "form-wrapper__left", children: _jsx(QScrollWrapper, { children: _jsxs(Grid, { className: className, children: [this.renderSaveButton(), this.renderPathways(), this.renderPathwaysBasedContextRules()] }) }) }), _jsx("div", { className: "form-wrapper__right", children: this.renderPathwaysBasedContextRulesInsights() })] }));
    };
    renderConfigurationInformation = () => {
        if (!this.state.editable)
            return _jsx(_Fragment, {});
        const className = _className(this.state.editMode && 'edit-mode');
        return (_jsx("div", { className: "form-wrapper", children: _jsx(QScrollWrapper, { children: _jsxs(Grid, { className: className, children: [this.renderSaveButton(), this.renderLabel(), this.renderKnowledgeBaseVersion(), this.renderAdvisorConfig()] }) }) }));
    };
    renderSaveButton = () => {
        const { editable, editMode } = this.state;
        if (!editable || editable?.item._id || !editMode)
            return;
        const disabled = !(editable.item.label && editable.item.kbVersionId);
        return _jsxs(_Fragment, { children: [_jsx(Button, { variant: 'primary', disabled: disabled, onClick: async () => {
                        try {
                            await editable?.save();
                        }
                        catch (err) {
                            ModuleFE_Toaster.toastError(`Could not create new configuration. Please validate the configuration and try again.`);
                            this.logError(err);
                            this.forceUpdate();
                        }
                    }, children: "Save" }), _jsx("div", {})] });
    };
    renderLabel = () => {
        const editable = this.state.editable;
        return _jsx(TS_PropRenderer.Flat, { label: 'Label', error: editable?.hasError('label'), children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: ViewKey_View, renderer: () => _jsx("div", { className: 'grid-data', children: editable?.item.label }) },
                    {
                        key: ViewKey_Edit,
                        renderer: () => _jsx(DefaultEditor_InputText, { editable: editable, prop: 'label' })
                    }
                ] }) });
    };
    renderKnowledgeBaseVersion = () => {
        const artifacts = ModuleFE_Artifact.cache.allMutable();
        const artifactVersionAdapter = SimpleListAdapter(sortArray(artifacts, item => versionStringToNumeric(item.dataVersion), true), item => _jsx("div", { children: item.item.dataVersion }));
        const editable = this.state.editable;
        const selectedArtifact = artifacts.find(artifact => artifact._id === editable.item.kbVersionId);
        return _jsx(TS_PropRenderer.Flat, { label: 'Knowledge Base Version', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    {
                        key: ViewKey_View,
                        renderer: () => _jsx("div", { className: 'grid-data', children: selectedArtifact?.dataVersion ?? '-' })
                    },
                    {
                        key: ViewKey_Edit,
                        renderer: () => _jsx(TS_DropDown, { adapter: artifactVersionAdapter, selected: selectedArtifact, onSelected: async (selected) => {
                                this.state.editable?.updateObj({ kbVersionId: selected._id });
                            } })
                    }
                ] }) });
    };
    renderAdvisorConfig = () => {
        const { editable, editMode } = this.state;
        const editablePrefixing = editable.editProp('aglFixing', {});
        return _jsx(_Fragment, { children: _jsxs(TS_PropRenderer.Flat, { label: 'Advisor Configuration', className: 'span-row', children: [_jsx(TS_PropRenderer.Flat, { label: 'Enable End Encounter Survey', className: 'indent', children: _jsx(LL_H_C, { className: 'grid-data', children: _jsx(TS_Toggle, { disabled: !editMode, checked: !!editable.item.enableSurvey, onCheck: () => {
                                    editable.updateObj({ enableSurvey: !editable.item.enableSurvey });
                                } }) }) }), _jsx(TS_PropRenderer.Flat, { label: 'Dev EHR Instance', className: 'indent', children: _jsx(LL_H_C, { className: 'grid-data', children: _jsx(TS_Toggle, { disabled: !editMode, checked: !!editable.item.devInstance, onCheck: () => {
                                    editable.updateObj({ devInstance: !editable.item.devInstance });
                                } }) }) }), _jsx(TS_PropRenderer.Flat, { label: 'Enable Documentation', className: 'indent', children: _jsx(LL_H_C, { className: 'grid-data', children: _jsx(TS_Toggle, { disabled: !editMode, checked: !!editable.item.enableDocumentation, onCheck: () => {
                                    editable.updateObj({ enableDocumentation: !editable.item.enableDocumentation });
                                } }) }) }), _jsx(TS_PropRenderer.Flat, { label: 'User EHR Order ID', className: 'indent', children: _jsx(LL_H_C, { className: 'grid-data', children: _jsx(TS_Toggle, { disabled: !editMode, checked: !!editable.item.useEhrOrderId, onCheck: () => {
                                    editable.updateObj({ useEhrOrderId: !editable.item.useEhrOrderId });
                                } }) }) }), _jsxs(TS_PropRenderer.Flat, { label: 'AGL', className: 'span-row', children: [_jsx(TS_PropRenderer.Flat, { label: 'Enable AGL', className: 'indent', children: _jsx(LL_H_C, { className: 'grid-data', children: _jsx(TS_Toggle, { disabled: !editMode, checked: !!editable.item.enableAgl, onCheck: () => {
                                            editable.updateObj({ enableAgl: !editable.item.enableAgl });
                                        } }) }) }), this.render_Config_Prefixing(editablePrefixing)] })] }) });
    };
    render_Config_Prefixing = (editable) => {
        const editableLabs = editable.editProp('labs', { enabled: false, suffix: '' });
        const editableRad = editable.editProp('radiology', { enabled: false, suffix: '' });
        const editMode = this.state.editMode;
        return _jsxs(_Fragment, { children: [_jsx(TS_PropRenderer.Flat, { label: 'Labs Fixing', className: 'indent', children: _jsxs(LL_H_C, { style: { gap: '8px' }, children: [_jsx(TS_Toggle, { disabled: !editMode, checked: editableLabs.get('enabled'), onCheck: () => editableLabs.updateObj({ enabled: !editableLabs.get('enabled') }) }), _jsx(DefaultEditor_InputText_Optional, { disabled: !editMode, editable: editableLabs, prop: 'suffix', placeholder: 'Suffix' })] }) }), _jsx(TS_PropRenderer.Flat, { label: 'Radiology Fixing', className: 'indent', children: _jsxs(LL_H_C, { style: { gap: '8px' }, children: [_jsx(TS_Toggle, { disabled: !editMode, checked: editableRad.get('enabled'), onCheck: () => editableRad.updateObj({ enabled: !editableRad.get('enabled') }) }), _jsx(DefaultEditor_InputText_Optional, { disabled: !editMode, editable: editableRad, prop: 'suffix', placeholder: 'Suffix' })] }) })] });
    };
    renderPathwaysBasedContextRules = () => {
        const editable = this.state.editable;
        const contextRuleIds = this.state.editable.item.contextRuleIds;
        return (_jsx(TS_PropRenderer.Flat, { label: 'Context Rules', className: 'span-row', children: _jsxs(_Fragment, { children: [_jsx("div", {}), _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                            {
                                key: ViewKey_View,
                                renderer: () => _jsx(PathwaysByContextRules, { editable: editable })
                            },
                            {
                                key: ViewKey_Edit,
                                renderer: () => _jsx(MultiSelect_ContextRule, { itemsDirection: 'vertical', editable: editable, prop: 'contextRuleIds', itemFilter: id => {
                                        const contextRule = ModuleFE_ContextRule.cache.unique(id);
                                        if (!contextRule) {
                                            ModuleFE_Toaster.toastError(`Could not find context rule with id ${id}`);
                                            return false;
                                        }
                                        const relevantContextRulesIds = ModuleFE_ContextRule.cache
                                            .filter(contextRule => contextRule.label.toLowerCase().includes('pathway'))
                                            .map(contextRule => contextRule._id);
                                        return relevantContextRulesIds?.includes(id) && !contextRuleIds?.includes(id);
                                    } })
                            }
                        ] })] }) }));
    };
    renderPathwaysBasedContextRulesInsights = () => {
        return (_jsx(TS_PropRenderer.Flat, { label: 'Selected Context Rules', className: 'span-row', children: _jsx(PathwaysByContextRules, { editable: this.state.editable, showPathways: true }) }));
    };
    renderPathways = () => {
        const contextRuleIds = this.props.editable.item.contextRuleIds;
        const selectedContextRules = getSelectedContextRulesIds(contextRuleIds);
        const pathways = ModuleFE_Pathway.getNonArchivedPathways();
        const pathwaysByContextRules = filterDuplicates(Object.values(getMatchingPathwaysByContextRules(pathways, selectedContextRules))
            .flat()
            .map(pathway => pathway._id));
        const pathwaysCategories = [
            { label: 'Chief Complaint', type: 'chief-complaint' },
            { label: 'Nurse Triage', type: 'triage' },
            { label: 'Differential Diagnosis', type: 'differential-diagnosis' },
            { label: 'Treatment', type: 'treatment' },
            { label: 'Single State', type: 'single-state' }
        ];
        return _jsx(TS_PropRenderer.Flat, { label: 'Pathways', className: 'span-row', children: pathwaysCategories.map((item, index) => _jsx(TS_PropRenderer.Flat, { label: item.label, className: 'indent', children: _jsx(LL_V_L, { children: this.getPathwaysOfType(item.type, pathwaysByContextRules).map(pathway => _jsx("div", { className: 'grid-data', children: pathway.label }, pathway._id)) }) }, index)) });
    };
    // ######################## Render - Notes Editor ########################
    render_Notes = () => {
        if (!this.state.editable)
            return;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Notes', children: _jsx(MultiSelect_NoteTemplate.sortable, { prop: 'noteIds', editable: this.state.editable, disabled: !this.state.editMode }) });
    };
    // ######################## Render - AB Testing Editor ########################
    render_ABTesting = () => {
        if (!this.state.editable)
            return;
        const { editMode } = this.state;
        const editable = this.state.editable.editProp('abTesting', {});
        return _jsx("div", { className: "form-wrapper", children: _jsx(QScrollWrapper, { children: _jsxs(Grid, { className: _className(editMode && 'edit-mode'), children: [_jsx(TS_PropRenderer.Flat, { label: 'Magic Wand', children: _jsx(LL_H_C, { className: 'grid-data', children: _jsx(Dropdown_ABTestValue, { editable: editable, prop: 'magicWand', disabled: !editMode }) }) }), _jsx(TS_PropRenderer.Flat, { label: 'Catch Up - Vitals', children: _jsx(LL_H_C, { className: 'grid-data', children: _jsx(Dropdown_ABTestValue, { editable: editable, prop: 'catchUp_vitals', disabled: !editMode }) }) }), _jsx(TS_PropRenderer.Flat, { label: 'Catch Up - Recap', children: _jsx(LL_H_C, { className: 'grid-data', children: _jsx(Dropdown_ABTestValue, { editable: editable, prop: 'catchUp_recap', disabled: !editMode }) }) }), _jsx(TS_PropRenderer.Flat, { label: 'Pertinent Data', children: _jsx(LL_H_C, { className: 'grid-data', children: _jsx(Dropdown_ABTestValue, { editable: editable, prop: 'pertinentData', disabled: !editMode }) }) })] }) }) });
    };
}
//# sourceMappingURL=Editor_Organization_AdvisorConfigurations.js.map